import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import RemoveRedEyeRoundedIcon from '@mui/icons-material/RemoveRedEyeRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import parse from 'autosuggest-highlight/parse';
import { isNull } from 'lodash';
import { GooglePlaceAddress } from 'types/user';
import { useDidMount } from 'hooks/useDidMount';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { GoogleAutocompleteMap } from 'components/@shared/GoogleAutocompleteMap';
import { Collapse, Fade, IconButton } from '@mui/material';

interface MainTextMatchedSubstrings {
    offset: number;
    length: number;
}
interface StructuredFormatting {
    main_text: string;
    secondary_text: string;
    main_text_matched_substrings?: readonly MainTextMatchedSubstrings[];
}
interface PlaceType {
    description: string;
    structured_formatting?: StructuredFormatting;
    place_id: string;
}

interface Props {
    value: GooglePlaceAddress | null;
    InputProps: {
        error: boolean;
        helperText?: string | false;
    };
    /** @param googleAddress {GooglePlaceAddress | null} Google map place_id and description */
    onChange: (googleAddress: GooglePlaceAddress | null) => void;
}

export const GoogleAutocomplete = memo(({ value: initialValue, InputProps, onChange }: Props) => {
    const [value, setValue] = useState<PlaceType | null>(null);
    const [showMap, setShowMap] = useState(false);
    const [options, setOptions] = useState<readonly PlaceType[]>([]);
    const [sessionToken, setSessionToken] = useState<google.maps.places.AutocompleteSessionToken>();
    const [autocompleteService, setAutocompleteService] = useState<google.maps.places.AutocompleteService | null>(null);

    const places = useMapsLibrary('places');

    const fetchPredictions = useCallback(
        async (inputValue: string) => {
            if (!autocompleteService || !inputValue) {
                setOptions([]);
                return;
            }

            const request = { input: inputValue, sessionToken };
            const response = await autocompleteService.getPlacePredictions(request);

            setOptions(response.predictions);
        },
        [autocompleteService, sessionToken],
    );

    const changeHandler = useCallback(
        async (event: any, newValue: PlaceType | null) => {
            if (!places) return;
            setOptions(newValue ? [newValue, ...options] : options);
            setValue(newValue);
            onChange(isNull(newValue) ? newValue : { placeId: newValue.place_id, description: newValue.description });
        },
        [autocompleteService, places, onChange],
    );

    const inputChangeHandler = useCallback(
        (event: React.SyntheticEvent<Element, Event>, newInputValue: string) => {
            fetchPredictions(newInputValue);
        },
        [fetchPredictions],
    );

    const toogleMapHandler = useCallback(() => {
        setShowMap((prevState) => !prevState);
    }, []);

    const mapPickHandler = useCallback((address: GooglePlaceAddress | null) => {
        if (address) {
            setValue({ place_id: address.placeId, description: address.description });
            onChange(address);
        }
    }, []);

    useEffect(() => {
        if (!places) return;
        setAutocompleteService(new places.AutocompleteService());
        setSessionToken(new places.AutocompleteSessionToken());

        return () => setAutocompleteService(null);
    }, [places]);

    useDidMount(() => {
        if (initialValue) {
            setValue({ place_id: initialValue.placeId, description: initialValue.description });
        }
    });

    return (
        <Box>
            <Box display="flex" alignItems="center" gap={1}>
                <Autocomplete
                    size="small"
                    id="google-map-autocomplete"
                    getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
                    filterOptions={(x) => x}
                    options={options}
                    autoComplete
                    includeInputInList
                    filterSelectedOptions
                    value={value}
                    noOptionsText="No locations"
                    sx={{ flex: 1 }}
                    onChange={changeHandler}
                    onInputChange={inputChangeHandler}
                    renderInput={(params) => <TextField {...params} {...InputProps} label="Add a location" fullWidth />}
                    renderOption={(props, option) => {
                        const matches = option.structured_formatting?.main_text_matched_substrings || [];

                        const parts = parse(
                            option.structured_formatting?.main_text || '',
                            matches.map((match: any) => [match.offset, match.offset + match.length]),
                        );

                        return (
                            <li {...props}>
                                <Grid container alignItems="center">
                                    <Grid item sx={{ display: 'flex', width: 44 }}>
                                        <LocationOnIcon sx={{ color: 'text.secondary' }} />
                                    </Grid>
                                    <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                                        {parts.map((part, index) => (
                                            <Box
                                                key={index}
                                                component="span"
                                                sx={{ fontWeight: part.highlight ? 'bold' : 'regular' }}
                                            >
                                                {part.text}
                                            </Box>
                                        ))}
                                        <Typography variant="body2" color="text.secondary">
                                            {option.structured_formatting?.secondary_text}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </li>
                        );
                    }}
                />
                <IconButton size="small" onClick={toogleMapHandler}>
                    {showMap ? <VisibilityOffRoundedIcon /> : <RemoveRedEyeRoundedIcon />}
                </IconButton>
            </Box>
            <Collapse in={showMap}>
                <GoogleAutocompleteMap placeId={value?.place_id} onChange={mapPickHandler} />
            </Collapse>
        </Box>
    );
});

GoogleAutocomplete.displayName = 'GoogleAutocomplete';
